import { useQuery } from '@tanstack/react-query';
import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

import { Spinner } from '@/components/ui';
import { useAuth } from '@/features/auth/useAuth';
import { api } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/errors';

interface GuardProps {
  children: ReactNode;
}

/**
 * Server-side admin check via `GET /api/v1/admin/me`.
 *
 * Hiding the Admin nav link is UX only — this guard is what keeps non-admins out.
 */
export function RequireAdmin({ children }: GuardProps) {
  const { isAuthenticated, isReady } = useAuth();
  const location = useLocation();

  const adminQuery = useQuery({
    queryKey: ['admin', 'me'],
    queryFn: ({ signal }) => api.admin.me(signal),
    enabled: isReady && isAuthenticated,
    staleTime: 60_000,
    retry: false,
  });

  if (!isReady || (isAuthenticated && adminQuery.isPending)) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Spinner size="lg" label="Checking admin access" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/sign-in" replace state={{ from: location.pathname }} />;
  }

  if (adminQuery.isError) {
    const error = adminQuery.error;
    if (error instanceof ApiError && error.isForbidden) {
      return <Navigate to="/403" replace />;
    }
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-2 px-4 text-center">
        <p className="text-sm text-danger-500">Could not verify admin access.</p>
      </div>
    );
  }

  return children;
}
